import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { Sparkle, ChartBar, EnvelopeSimple, Phone } from "@phosphor-icons/react";

const FRIENDLY = {
  q1_age_bracket: { "25_34": "25-34", "35_44": "35-44", "45_54": "45-54", "55_64": "55-64", "65_plus": "65+" },
  q2_primary_goal: {
    income_for_life: "Guaranteed paycheck for life",
    tax_free_wealth: "Tax-free retirement bucket",
    family_protection: "Family protection",
    generational_legacy: "Generational legacy",
    wealth_while_alive: "Wealth while alive",
  },
  q3_household_income: {
    under_60k: "<$60k", "60_100k": "$60-100k", "100_200k": "$100-200k",
    "200_500k": "$200-500k", "500k_plus": "$500k+",
  },
  q7_dream_outcome: {
    retired_early: "Retire 10 years early",
    tax_freedom: "Tax-free retirement income",
    business_freedom: "Quit job, own business",
    family_secured: "Kids' futures secured",
    passive_income: "Market-independent income",
  },
  q8_action_window: {
    this_month: "This month",
    "30_60_days": "30-60 days",
    this_year: "This year",
    researching: "Researching",
  },
};

const label = (qid, val) => (FRIENDLY[qid] && FRIENDLY[qid][val]) || val || "—";

export default function QuizInsights() {
  const [responses, setResponses] = useState([]);
  const [insights, setInsights] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/quiz/responses").then((r) => setResponses(r.data || []));
    api.get("/quiz/insights").then((r) => setInsights(r.data));
  }, []);

  const hot = useMemo(() => {
    if (!insights) return null;
    const top = (obj, qid) => {
      const e = Object.entries(obj || {});
      if (!e.length) return null;
      e.sort((a, b) => b[1] - a[1]);
      const [k, v] = e[0];
      return { label: qid ? label(qid, k) : k, count: v };
    };
    return {
      goal: top(insights.top_goal, "q2_primary_goal"),
      dream: top(insights.top_dream, "q7_dream_outcome"),
      window: top(insights.action_window, "q8_action_window"),
      primary: top(insights.primary_fit, null),
    };
  }, [insights]);

  return (
    <DashboardLayout>
      <div data-testid="quiz-insights-page" className="p-8 lg:p-12 max-w-7xl">
        <div className="flex items-center gap-3 mb-2">
          <Sparkle size={22} className="text-exodus-gold" weight="fill" />
          <p className="eyebrow text-exodus-muted">Customer Intake Funnel</p>
        </div>
        <h1 className="font-display text-4xl lg:text-5xl">Quiz Insights</h1>
        <p className="text-sm text-exodus-muted mt-2">
          {insights?.total ?? 0} responses · live recommendations &amp; hotspots
        </p>

        {/* Hotspot cards */}
        {insights && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-exodus-border mt-10">
            <HotCard testid="hot-total" label="Total responses" value={insights.total} sub="all-time" />
            <HotCard testid="hot-primary" label="Top product fit" value={hot?.primary?.label || "—"} sub={hot?.primary ? `${hot.primary.count} matches` : ""} />
            <HotCard testid="hot-goal" label="Most-picked goal" value={hot?.goal?.label || "—"} sub={hot?.goal ? `${hot.goal.count} clients` : ""} />
            <HotCard testid="hot-window" label="Avg action window" value={hot?.window?.label || "—"} sub={hot?.window ? `${hot.window.count} clients` : ""} />
          </div>
        )}

        {/* Distribution bars */}
        {insights && (
          <div className="grid lg:grid-cols-2 gap-px bg-exodus-border mt-px">
            <Distribution
              testid="dist-fit"
              title="Primary product fit"
              data={insights.primary_fit}
            />
            <Distribution
              testid="dist-window"
              title="Action window"
              data={insights.action_window}
              labelFn={(k) => label("q8_action_window", k)}
            />
            <Distribution
              testid="dist-goal"
              title="Primary goal"
              data={insights.top_goal}
              labelFn={(k) => label("q2_primary_goal", k)}
            />
            <Distribution
              testid="dist-dream"
              title="Dream outcome"
              data={insights.top_dream}
              labelFn={(k) => label("q7_dream_outcome", k)}
            />
          </div>
        )}

        {/* Responses table */}
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <ChartBar size={18} className="text-exodus-gold" />
            <h2 className="font-display text-2xl">All responses</h2>
          </div>

          <div data-testid="quiz-responses-table" className="border border-exodus-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-exodus-surface text-left text-xs eyebrow text-exodus-muted">
                  <th className="px-5 py-3">When</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Primary fit</th>
                  <th className="px-5 py-3">Tier</th>
                  <th className="px-5 py-3">Window</th>
                  <th className="px-5 py-3">Next step</th>
                </tr>
              </thead>
              <tbody>
                {responses.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-exodus-muted">No responses yet.</td></tr>
                )}
                {responses.map((r) => {
                  const rec = r.recommendation || {};
                  return (
                    <tr
                      key={r.id}
                      data-testid={`quiz-response-row-${r.id}`}
                      onClick={() => setActive(r)}
                      className="border-t border-exodus-border hover:bg-exodus-surface cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-3 text-xs text-exodus-muted">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-5 py-3 font-medium">{r.name}</td>
                      <td className="px-5 py-3 text-exodus-muted">{r.email}</td>
                      <td className="px-5 py-3 gold-text">{rec.primary_label}</td>
                      <td className="px-5 py-3"><span className="text-xs border border-exodus-border px-2 py-1">{rec.tier}</span></td>
                      <td className="px-5 py-3 text-xs">{label("q8_action_window", r.answers?.q8_action_window)}</td>
                      <td className="px-5 py-3 text-xs uppercase tracking-wide text-exodus-gold">{rec.next_step}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail drawer */}
        {active && (
          <div
            data-testid="quiz-response-detail"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end"
            onClick={() => setActive(null)}
          >
            <div
              className="w-full max-w-xl bg-exodus-bg border-l border-exodus-border overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 border-b border-exodus-border">
                <button
                  data-testid="quiz-detail-close"
                  onClick={() => setActive(null)}
                  className="text-xs text-exodus-muted hover:text-exodus-gold mb-6"
                >
                  ← Close
                </button>
                <h3 className="font-display text-3xl">{active.name}</h3>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-exodus-muted">
                  <span className="flex items-center gap-2"><EnvelopeSimple size={14} /> {active.email}</span>
                  {active.phone && <span className="flex items-center gap-2"><Phone size={14} /> {active.phone}</span>}
                </div>
              </div>

              <div className="p-8 border-b border-exodus-border">
                <p className="eyebrow gold-text mb-3">Recommendation</p>
                <p className="font-display text-2xl">{active.recommendation?.primary_label}</p>
                {active.recommendation?.secondary && (
                  <p className="text-sm text-exodus-muted mt-2">Secondary: {active.recommendation.secondary}</p>
                )}
                <p className="text-xs text-exodus-muted mt-3">
                  Tier: {active.recommendation?.tier} · Range: {active.recommendation?.estimated_premium_range} ·
                  Next step: <span className="gold-text uppercase">{active.recommendation?.next_step}</span>
                </p>
                <ul className="mt-5 space-y-2">
                  {(active.recommendation?.rationale || []).map((r, i) => (
                    <li key={i} className="text-sm text-exodus-text flex gap-2"><span className="gold-text">—</span> {r}</li>
                  ))}
                </ul>
              </div>

              <div className="p-8">
                <p className="eyebrow mb-4">Full answers</p>
                <dl className="space-y-3">
                  {Object.entries(active.answers || {}).map(([k, v]) => (
                    <div key={k} className="border-b border-exodus-border/40 pb-2">
                      <dt className="text-xs text-exodus-muted">{k}</dt>
                      <dd className="text-sm text-exodus-text mt-1">{Array.isArray(v) ? v.map((x) => label(k, x)).join(", ") : label(k, v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function HotCard({ testid, label, value, sub }) {
  return (
    <div data-testid={testid} className="bg-exodus-surface p-6">
      <p className="eyebrow text-xs text-exodus-muted">{label}</p>
      <p className="font-display text-2xl mt-3 leading-tight">{value}</p>
      {sub && <p className="text-xs text-exodus-muted mt-2">{sub}</p>}
    </div>
  );
}

function Distribution({ testid, title, data, labelFn }) {
  const entries = Object.entries(data || {});
  const total = entries.reduce((acc, [, v]) => acc + v, 0) || 1;
  entries.sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid={testid} className="bg-exodus-surface p-6">
      <p className="eyebrow text-xs text-exodus-muted mb-4">{title}</p>
      <div className="space-y-3">
        {entries.length === 0 && <p className="text-xs text-exodus-muted">No data yet.</p>}
        {entries.map(([k, v]) => {
          const pct = Math.round((v / total) * 100);
          return (
            <div key={k}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-exodus-text">{labelFn ? labelFn(k) : k}</span>
                <span className="text-exodus-muted">{v} · {pct}%</span>
              </div>
              <div className="h-1.5 bg-exodus-elevated">
                <div className="h-full bg-exodus-gold" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
